import { useState } from 'react';
import Dialog, { DialogError } from '../../components/Dialog.jsx';
import { api } from '../../lib/api.js';
import { useUnsavedChanges } from '../../lib/use-unsaved-changes.js';
import { AUDITOR_ROLE, TENANT_ROLES } from '../../../../shared/solution.mjs';
import { GUARDIAN_ROLE, isStaffEmail } from '../../lib/people-rows.js';
import { AUDITOR_DEFAULT_DAYS, GUARDIAN_DIRECTORY_HINT, RoleChoices, assignableIds, auditorExpiresAt, validAuditorDays } from './people-format.jsx';

const same = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

/**
 * Manage roles of one person (spec, "Guardian as a role"). The portal roles
 * go through PATCH /people first (the last-admin guard can refuse), then
 * Guardian goes through the Watch guardian routes. Two services, not atomic:
 * a Guardian failure after saved roles is shown as such and the lists reload.
 * Mount it only while it is open (the state starts from `row`).
 */
export default function ManageRolesDialog({ row, customerId, canGuardian, tenantLocked = false, auditorSelectable = true, viewerSelectable = true, guardianCount = 0, maxGuardians = 10, onClose, onDone, returnFocusRef = null }) {
  const [selected, setSelected] = useState(() => new Set([...row.tenantRoles, ...(row.guardian ? [GUARDIAN_ROLE] : [])]));
  const heldAuditor = row.tenantRoles.includes(AUDITOR_ROLE);
  // A new auditor grant starts at 30 days; a held one keeps its end date unless a number is typed.
  const [auditorDays, setAuditorDays] = useState(heldAuditor ? '' : AUDITOR_DEFAULT_DAYS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const isMember = row.kind === 'member';
  const tenantAfter = TENANT_ROLES.filter((r) => selected.has(r));
  const tenantChanged = isMember && !tenantLocked && !same(tenantAfter, row.tenantRoles);
  const auditorAfter = tenantAfter.includes(AUDITOR_ROLE);
  const auditorDaysOk = !auditorAfter || (heldAuditor && auditorDays === '') || validAuditorDays(auditorDays);
  const renewAuditor = isMember && !tenantLocked && auditorAfter && heldAuditor && auditorDays !== '';
  const sendTenant = tenantChanged || renewAuditor;
  const guardianAfter = selected.has(GUARDIAN_ROLE);
  const guardianChanged = canGuardian && guardianAfter !== Boolean(row.guardian);
  const needsRole = isMember && !tenantLocked && tenantAfter.length === 0;
  const guardianFull = canGuardian && !row.guardian && guardianCount >= maxGuardians;
  const canSave = !busy && (sendTenant || guardianChanged) && !needsRole && auditorDaysOk;
  useUnsavedChanges(sendTenant || guardianChanged);

  const toggle = (id, on) => {
    setError(null);
    setSelected((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });
  };

  // The parent's reload may reject; that must never strand the dialog on "Saving...".
  const notify = async (result) => {
    try { await onDone(result); } catch { /* the save itself already happened; the parent shows its own reload error */ }
  };

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (sendTenant) {
        try {
          // A staff member has no pool account to look up, so the roles this edit was decided on travel with it.
          const expected = isStaffEmail(row.email) ? [row.member.roles] : [];
          // The expiry follows the Auditor role only (a new grant, or a renewal typed for a held one).
          const expiry = auditorAfter && auditorDays !== '' ? [expected[0], auditorExpiresAt(auditorDays)] : expected;
          await api.updatePersonRoles(customerId, row.email, tenantAfter, ...expiry);
        } catch (e) {
          setError(e?.message || 'The roles could not be saved.');
          return;
        }
      }
      if (guardianChanged) {
        try {
          if (guardianAfter) await api.addWatchGuardian(customerId, row.email);
          else await api.removeWatchGuardian(customerId, row.guardian.guardianId);
        } catch (e) {
          const why = e?.message || 'The Guardian change could not be saved.';
          setError(sendTenant ? `Roles saved. Guardian was not changed: ${why}` : why);
          await notify({ roles: sendTenant, guardian: false, partial: true });
          return;
        }
      }
      await notify({ roles: sendTenant, guardian: guardianChanged, partial: false });
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      title={`Manage roles for ${row.email}`}
      onClose={() => { if (!busy) onClose(); }}
      returnFocusRef={returnFocusRef}
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary" disabled={!canSave} onClick={save}>{busy ? 'Saving...' : 'Save'}</button>
        </>
      )}
    >
      {isMember ? null : (
        <p className="text-xs text-ipc-muted">This person is known from the Microsoft Entra directory only. To give portal roles, invite this person.</p>
      )}
      <RoleChoices
        idPrefix="mr"
        ids={assignableIds({ auditorSelectable, viewerSelectable, canGuardian, held: row.tenantRoles })}
        selected={selected}
        onToggle={toggle}
        showPortal={isMember}
        portalDisabled={tenantLocked || busy}
        auditorDays={auditorDays}
        onAuditorDays={setAuditorDays}
        auditorOptional={heldAuditor}
        guardianDisabled={guardianFull || busy}
        guardianNote={guardianFull ? `At most ${maxGuardians} guardians. Remove one first.` : null}
        portalNotes={(
          <>
            {tenantLocked ? <p className="text-xs text-ipc-muted">Managed by IPC Global: their portal roles cannot be changed here.</p> : null}
            {needsRole ? <p className="text-xs text-ipc-muted">Choose at least one portal role, or revoke their access instead.</p> : null}
          </>
        )}
        watchNotes={guardianAfter && !row.guardian ? <p className="text-xs text-ipc-muted">{GUARDIAN_DIRECTORY_HINT}</p> : null}
      />
      <DialogError message={error} />
    </Dialog>
  );
}
